import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const VERSIONS = [
  { version: 'v1.2', date: '03 Mar 2026', generatedBy: 'James Donovan', current: true },
  { version: 'v1.1', date: '25 Feb 2026', generatedBy: 'James Donovan', current: false },
  { version: 'v1.0', date: '18 Feb 2026', generatedBy: 'Sarah Mitchell', current: false },
];

export default function TermSheetTab({ deal, showToast }) {
  const [showSendModal, setShowSendModal] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState('');
  const [message, setMessage] = useState('');

  const handleDownload = () => showToast('Term sheet exported', 'success');
  const handleCopyLink = () => {
    navigator.clipboard?.writeText(window.location?.href)?.catch(() => {});
    showToast('Link copied to clipboard', 'success');
  };
  const handleSend = () => {
    setShowSendModal(false);
    setRecipientEmail('');
    setMessage('');
    showToast('Term sheet sent for review', 'success');
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Term Sheet Preview */}
        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
            {/* Document Header */}
            <div className="bg-slate-800 text-white px-8 py-6">
              <div className="flex items-center justify-between mb-4">
                <img src="/assets/images/SeedDrive_Black_-1772706977187.png" alt="SeedDrive" className="h-7 brightness-0 invert" />
                <span className="text-xs text-slate-300 font-medium tracking-wider">CONFIDENTIAL</span>
              </div>
              <h2 className="text-xl font-bold">INDICATIVE TERM SHEET</h2>
              <p className="text-slate-300 text-sm mt-1">SAMPLE DATA — FOR DEMONSTRATION PURPOSES ONLY</p>
            </div>

            <div className="px-8 py-6 space-y-6 text-sm">
              {/* Transaction Overview */}
              <section>
                <h3 className="font-bold text-foreground text-base border-b border-border pb-2 mb-3">1. Transaction Overview</h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                  <div><span className="text-muted-foreground">Deal Name:</span> <span className="font-medium text-foreground">{deal?.name}</span></div>
                  <div><span className="text-muted-foreground">Structure Type:</span> <span className="font-medium text-foreground">{deal?.structureType}</span></div>
                  <div><span className="text-muted-foreground">Total Deal Size:</span> <span className="font-medium text-foreground">£{deal?.totalDealSize}m</span></div>
                  <div><span className="text-muted-foreground">Status:</span> <span className="font-medium text-foreground">{deal?.status}</span></div>
                  <div><span className="text-muted-foreground">Sponsor:</span> <span className="font-medium text-foreground">SeedDrive GP Ltd</span></div>
                  <div><span className="text-muted-foreground">Date:</span> <span className="font-medium text-foreground">March 2026</span></div>
                </div>
              </section>

              {/* Capital Structure */}
              <section>
                <h3 className="font-bold text-foreground text-base border-b border-border pb-2 mb-3">2. Capital Structure</h3>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-1 text-muted-foreground font-semibold">Tranche</th>
                      <th className="text-right py-1 text-muted-foreground font-semibold">Amount</th>
                      <th className="text-right py-1 text-muted-foreground font-semibold">% of Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    <tr><td className="py-1.5 text-foreground">Senior Debt</td><td className="text-right">£{deal?.debtTranche}m</td><td className="text-right">{deal?.debtPct}%</td></tr>
                    <tr><td className="py-1.5 text-foreground">Equity (Preferred + Common)</td><td className="text-right">£{deal?.equityTranche}m</td><td className="text-right">{deal?.equityPct}%</td></tr>
                    <tr className="font-semibold border-t-2 border-border"><td className="py-1.5">Total</td><td className="text-right">£{deal?.totalDealSize}m</td><td className="text-right">100%</td></tr>
                  </tbody>
                </table>
              </section>

              {/* Key Economic Terms */}
              <section>
                <h3 className="font-bold text-foreground text-base border-b border-border pb-2 mb-3">3. Key Economic Terms</h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                  <div><span className="text-muted-foreground">Target IRR:</span> <span className="font-medium text-foreground">{deal?.targetIRR}%</span></div>
                  <div><span className="text-muted-foreground">Hurdle Rate:</span> <span className="font-medium text-foreground">{deal?.hurdleRate}%</span></div>
                  <div><span className="text-muted-foreground">Carried Interest:</span> <span className="font-medium text-foreground">{deal?.carry}%</span></div>
                  <div><span className="text-muted-foreground">Management Fee:</span> <span className="font-medium text-foreground">1.5% p.a.</span></div>
                  <div><span className="text-muted-foreground">Waterfall Type:</span> <span className="font-medium text-foreground">European</span></div>
                  <div><span className="text-muted-foreground">Clawback:</span> <span className="font-medium text-foreground">Yes</span></div>
                </div>
              </section>

              {/* Waterfall Summary */}
              <section>
                <h3 className="font-bold text-foreground text-base border-b border-border pb-2 mb-3">4. Waterfall Summary</h3>
                <ol className="list-decimal list-inside space-y-1 text-foreground">
                  <li>Return of Capital — 100% to investors</li>
                  <li>Preferred Return — {deal?.hurdleRate}% p.a. to investors</li>
                  <li>GP Catch-up — until GP receives {deal?.carry}% of profits</li>
                  <li>Residual Split — {100 - (deal?.carry || 20)}% investors / {deal?.carry}% GP</li>
                </ol>
              </section>

              {/* Legal Structure */}
              <section>
                <h3 className="font-bold text-foreground text-base border-b border-border pb-2 mb-3">5. Legal Structure</h3>
                <p className="text-foreground">SeedDrive Infrastructure Fund III (Cayman LP) → SD Mersey HoldCo Ltd (England & Wales) → {deal?.name} SPV Ltd (England & Wales)</p>
                <p className="text-muted-foreground mt-1">Governing Law: England & Wales · Dispute Resolution: LCIA</p>
              </section>

              {/* Conditions Precedent */}
              <section>
                <h3 className="font-bold text-foreground text-base border-b border-border pb-2 mb-3">6. Key Conditions Precedent</h3>
                <ul className="list-disc list-inside space-y-1 text-foreground">
                  <li>Planning consent confirmation</li>
                  <li>Environmental impact assessment sign-off</li>
                  <li>Grid connection agreement executed</li>
                  <li>Financial model audit completed</li>
                  <li>Lender credit committee approval</li>
                </ul>
              </section>

              {/* Disclaimer */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-xs text-slate-500 italic">
                This term sheet is indicative only and for demonstration purposes. It does not constitute a binding offer or commitment to provide financing. All figures are sample data only. Any reliance on this document is at the reader's own risk.
              </div>
            </div>
          </div>
        </div>

        {/* Actions + Version History */}
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold text-foreground mb-4">Actions</h3>
            <div className="space-y-2">
              <button onClick={handleDownload} className="flex items-center gap-2 w-full px-4 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
                <Icon name="Download" size={16} /> Download Term Sheet (PDF)
              </button>
              <button onClick={handleCopyLink} className="flex items-center gap-2 w-full px-4 py-2.5 rounded-lg border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors">
                <Icon name="Link" size={16} /> Copy Share Link
              </button>
              <button onClick={() => setShowSendModal(true)} className="flex items-center gap-2 w-full px-4 py-2.5 rounded-lg border border-border text-foreground text-sm font-medium hover:bg-muted transition-colors">
                <Icon name="Send" size={16} /> Send for Review
              </button>
            </div>
          </div>

          {/* Version History */}
          <div className="bg-card border border-border rounded-xl p-5">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="History" size={16} color="var(--color-primary)" />
              Version History
            </h3>
            <div className="space-y-2">
              {VERSIONS?.map((v) => (
                <div key={v?.version} className={`p-3 rounded-lg border ${
                  v?.current ? 'border-primary bg-primary/5' : 'border-border bg-muted/20'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">{v?.version}</span>
                    {v?.current && <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded-full">Current</span>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{v?.date} · {v?.generatedBy}</p>
                  {!v?.current && (
                    <button onClick={() => showToast(`Restored to ${v?.version}`, 'success')} className="mt-1 text-xs text-primary hover:underline">Restore</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      {/* Send Modal */}
      {showSendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-card border border-border rounded-xl p-6 w-full max-w-md shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-foreground">Send Term Sheet for Review</h3>
              <button onClick={() => setShowSendModal(false)} className="text-muted-foreground hover:text-foreground">
                <Icon name="X" size={18} />
              </button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground">Recipient Email</label>
                <input type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e?.target?.value)} placeholder="investor@example.com"
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Message (optional)</label>
                <textarea value={message} onChange={(e) => setMessage(e?.target?.value)} rows={3} placeholder="Please review the attached indicative term sheet..."
                  className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-lg bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none" />
              </div>
              <div className="flex gap-2 pt-2">
                <button onClick={() => setShowSendModal(false)} className="flex-1 px-4 py-2 border border-border rounded-lg text-sm text-foreground hover:bg-muted transition-colors">Cancel</button>
                <button onClick={handleSend} className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">Send</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
